import React, {
  createContext,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useCartContext } from './cartContext';

const CartStatusContext = createContext(null);

export const createCartStatusStore = () => {
  let items = new Map();
  const statuses = new Map();
  const listeners = new Map();
  return {
    hasItems: () => items.size > 0,
    getCartItem: (id) => items.get(id) || null,
    getCartStatus: (id) => statuses.get(id) || null,
    getProductQuantity: (id) => items.get(id)?.quantity,
    isInCart: (id) => items.has(id),
    subscribe: (id, listener) => {
      if (!listeners.has(id)) listeners.set(id, new Set());
      const group = listeners.get(id);
      group.add(listener);
      return () => {
        group.delete(listener);
        if (!group.size) listeners.delete(id);
      };
    },
    update: (next) => {
      const previous = items;
      items = next;
      const changed = new Set();
      previous.forEach((item, id) => {
        if (next.get(id) !== item) changed.add(id);
      });
      next.forEach((item, id) => {
        if (previous.get(id) !== item) changed.add(id);
      });
      changed.forEach((id) => {
        const item = next.get(id);
        if (item)
          statuses.set(id, {
            inCart: true,
            quantity: item.quantity,
            discount2: item.discount2,
            packageQuantity: item.packageQuantity,
          });
        else statuses.delete(id);
        const subscribers = listeners.get(id);
        if (subscribers) subscribers.forEach((listener) => listener());
      });
    },
    getSubscriberCount: () =>
      Array.from(listeners.values()).reduce(
        (total, group) => total + group.size,
        0,
      ),
  };
};

export const CartStatusProvider = ({ children }) => {
  const { cartLookupMap } = useCartContext();
  const storeRef = useRef(null);
  if (!storeRef.current) {
    storeRef.current = createCartStatusStore();
    storeRef.current.update(cartLookupMap);
  }
  useLayoutEffect(() => {
    storeRef.current.update(cartLookupMap);
  }, [cartLookupMap]);
  return (
    <CartStatusContext.Provider value={storeRef.current}>
      {children}
    </CartStatusContext.Provider>
  );
};

export const useCartStatus = () => {
  const store = useContext(CartStatusContext);
  if (!store)
    throw new Error('useCartStatus must be used within CartStatusProvider');
  return store;
};

const useCartSnapshot = (productId, method) => {
  const store = useCartStatus();
  const [, refresh] = useState(0);
  const snapshot = store[method](productId);
  useLayoutEffect(() => {
    let current = snapshot;
    const update = () => {
      const next = store[method](productId);
      if (current !== next) {
        current = next;
        refresh((value) => value + 1);
      }
    };
    const unsubscribe = store.subscribe(productId, update);
    update();
    return unsubscribe;
  }, [store, productId, method, snapshot]);
  return snapshot;
};

export const useProductCartStatus = (productId) =>
  useCartSnapshot(productId, 'getCartStatus');
export const useCartItem = (productId) =>
  useCartSnapshot(productId, 'getCartItem');
export { CartStatusContext };
